//* Import needed modules
import { api } from "../../../services/apis/configs/axiosConfigs";
import { API_URI } from "../../../constants/consts";

//? Types for ApplicationDiagrams
import {
  type ApplicationDiagram,
  type GetApplicationDiagramsResponse,
  type CreateApplicationDiagramInput,
  type ApplicationDiagramDetails,
} from "./types/types";

//? Types for Pagination
import { type Pagination } from "../../../services/stores/types";

export const applicationDiagramAPI = {
  /**
   * Get all ApplicationDiagrams within an application.
   *
   * @param applicationId - ID da aplicação.
   * @param search - Termo de busca opcional.
   * @param page - Número da página para paginação.
   * @param token - Token de autenticação.
   * @param perPage - Número de itens por página.
   * @returns Uma promessa que resolve para GetApplicationDiagramsResponse.
   */
  getAll: async function (
    applicationId: string,
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<GetApplicationDiagramsResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/applications/${applicationId}/diagrams?page=${page}&perPage=${perPage}${searchQuery}`;

    

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        console.log("AQIO" ,data);

        const pagination: Pagination = data.pagination || {
          currentPage: 0,
          totalPages: 0,
          itemsPerPage: 0,
          totalItems: 0,
        };

        return {
          applicationDiagrams: data.data || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching application diagrams:", error);
        throw new Error("Failed to fetch application diagrams");
      });
  },

  /**
   * Get a single ApplicationDiagram by ID.
   *
   * @param applicationId - ID da aplicação.
   * @param id - ID do ApplicationDiagram.
   * @param token - Token de autenticação.
   * @returns Uma promessa que resolve para ApplicationDiagramDetails.
   */
  getById: async function (
    applicationId: string,
    id: string,
    token: string
  ): Promise<ApplicationDiagramDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/diagrams/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error fetching application diagram:", error);
        throw new Error("Failed to fetch application diagram");
      });
  },

  /**
   * Create a new ApplicationDiagram within an application.
   *
   * @param applicationId - ID da aplicação.
   * @param data - Dados para criar o ApplicationDiagram.
   * @param token - Token de autenticação.
   * @returns Uma promessa que resolve para ApplicationDiagram.
   */
  create: async function (
    applicationId: string,
    data: CreateApplicationDiagramInput,
    token: string
  ): Promise<ApplicationDiagram> {
    const payload = {
      ...data,
      application_id: applicationId,
    };

    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/diagrams`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error creating application diagram:", error, data);
        throw error;
      });
  },

  /**
   * Update an existing ApplicationDiagram within an application.
   *
   * @param applicationId - ID da aplicação.
   * @param id - ID do ApplicationDiagram.
   * @param data - Dados para atualizar o ApplicationDiagram.
   * @param token - Token de autenticação.
   * @returns Uma promessa que resolve para ApplicationDiagram.
   */
  update: async function (
    applicationId: string,
    id: string,
    data: CreateApplicationDiagramInput,
    token: string
  ): Promise<ApplicationDiagram> {
    const payload = {
      ...data,
      application_id: applicationId,
    };

    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/diagrams/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating application diagram:", error);
        throw new Error("Failed to update application diagram");
      });
  },

  /**
   * Delete an ApplicationDiagram within an application.
   *
   * @param applicationId - ID da aplicação.
   * @param id - ID do ApplicationDiagram.
   * @param token - Token de autenticação.
   * @returns Uma promessa que resolve para void.
   */
  delete: async function (
    applicationId: string,
    id: string,
    token: string
  ): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/diagrams/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting application diagram:", error);
        throw new Error("Failed to delete application diagram");
      });
  },
};
